import React, { useState, useEffect } from "react";
import { Sidebar } from "../../components/Sidebar";
import {
  Bot,
  Pause,
  Settings,
  MoreVertical,
  ArrowLeft,
  CheckCircle,
  Loader2,
  XCircle,
  AlertTriangle,
  Menu,
} from "lucide-react";
import { Button } from "../../components/ui/button";
import { Avatar, AvatarFallback } from "../../components/ui/avatar";
import { useParams, Link, useNavigate } from "@tanstack/react-router";
import { sampleAgent } from "./index";
import { cn } from "../../lib/utils";
import { sampleAgents } from "../AIAgentsPage";

interface AgentLayoutProps {
  children: React.ReactNode;
  activeTab: string;
}

export const AgentLayout: React.FC<AgentLayoutProps> = ({
  children,
  activeTab,
}) => {
  const { agentId } = useParams({ from: "/ai-agents/$agentId" });
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [agent, setAgent] = useState<any>(null);
  const [isLoading, setIsLoading] = useState(true);

  const toggleSidebar = () => setIsSidebarOpen((v) => !v);
  const closeSidebar = () => setIsSidebarOpen(false);
  const navigate = useNavigate();

  // Load agent data from sessionStorage
  useEffect(() => {
    try {
      const storedAgents = sessionStorage.getItem("ai-agents");
      if (storedAgents) {
        const agents = JSON.parse(storedAgents).map((agent: any) => ({
          ...agent,
          lastActive: new Date(agent.lastActive),
        }));

        // Find the specific agent by ID
        const foundAgent = agents.find((a: any) => a.id === agentId);
        if (foundAgent) {
          setAgent(foundAgent);
        } else {
          // Fallback to sample agents if not found in sessionStorage
          const sampleAgent = sampleAgents.find((a: any) => a.id === agentId);
          if (sampleAgent) {
            setAgent(sampleAgent);
          } else {
            // Create a default agent if not found anywhere
            setAgent({
              id: agentId,
              name: "AI Agent",
              description: "AI agent for testing and evaluation",
              status: "active" as const,
              type: "Test AI",
              performance: {
                accuracy: 0,
                responseTime: 0,
                successRate: 0,
                totalRequests: 0,
                apiPerformance: {
                  latencyOverTime: [],
                  latencyByRequestCount: [],
                  successRateByHour: [],
                  errorBreakdown: {
                    timeoutErrors: 0,
                    rateLimitErrors: 0,
                    authenticationErrors: 0,
                    serverErrors: 0,
                    networkErrors: 0,
                    totalErrors: 0,
                  },
                  throughputMetrics: {
                    requestsPerMinute: 0,
                    requestsPerHour: 0,
                    peakRequestsPerMinute: 0,
                    averageConcurrentRequests: 0,
                  },
                },
              },
              lastActive: new Date(),
              version: "v1.0.0",
              model: "GPT-4",
              tags: ["testing"],
              category: "conversational" as const,
              config: {
                endpoint: "",
                apiKey: "",
                timeout: 30000,
                maxTokens: 4000,
                temperature: 0.7,
                modelVersion: "gpt-4",
                features: [],
              },
              metrics: {
                dailyRequests: 0,
                averageResponseTime: 0,
                errorRate: 0,
                uptime: 0,
                costPerRequest: 0,
                totalCost: 0,
              },
              testResults: {
                totalTests: 0,
                passedTests: 0,
                failedTests: 0,
                lastTestRun: new Date(),
                averageScore: 0,
              },
              testRuns: [],
            });
          }
        }
      } else {
        // No sessionStorage data, check sample agents
        const sampleAgent = sampleAgents.find((a: any) => a.id === agentId);
        if (sampleAgent) {
          setAgent(sampleAgent);
        } else {
          // Create default agent
          setAgent({
            id: agentId,
            name: "AI Agent",
            description: "AI agent for testing and evaluation",
            status: "active" as const,
            type: "Test AI",
            performance: {
              accuracy: 0,
              responseTime: 0,
              successRate: 0,
              totalRequests: 0,
              apiPerformance: {
                latencyOverTime: [],
                latencyByRequestCount: [],
                successRateByHour: [],
                errorBreakdown: {
                  timeoutErrors: 0,
                  rateLimitErrors: 0,
                  authenticationErrors: 0,
                  serverErrors: 0,
                  networkErrors: 0,
                  totalErrors: 0,
                },
                throughputMetrics: {
                  requestsPerMinute: 0,
                  requestsPerHour: 0,
                  peakRequestsPerMinute: 0,
                  averageConcurrentRequests: 0,
                },
              },
            },
            lastActive: new Date(),
            version: "v1.0.0",
            model: "GPT-4",
            tags: ["testing"],
            category: "conversational" as const,
            config: {
              endpoint: "",
              apiKey: "",
              timeout: 30000,
              maxTokens: 4000,
              temperature: 0.7,
              modelVersion: "gpt-4",
              features: [],
            },
            metrics: {
              dailyRequests: 0,
              averageResponseTime: 0,
              errorRate: 0,
              uptime: 0,
              costPerRequest: 0,
              totalCost: 0,
            },
            testResults: {
              totalTests: 0,
              passedTests: 0,
              failedTests: 0,
              lastTestRun: new Date(),
              averageScore: 0,
            },
            testRuns: [],
          });
        }
      }
    } catch (error) {
      console.error("Error loading agent data:", error);
      // Set a default agent on error
      setAgent({
        id: agentId,
        name: "AI Agent",
        description: "AI agent for testing and evaluation",
        status: "active" as const,
        type: "Test AI",
        performance: {
          accuracy: 0,
          responseTime: 0,
          successRate: 0,
          totalRequests: 0,
          apiPerformance: {
            latencyOverTime: [],
            latencyByRequestCount: [],
            successRateByHour: [],
            errorBreakdown: {
              timeoutErrors: 0,
              rateLimitErrors: 0,
              authenticationErrors: 0,
              serverErrors: 0,
              networkErrors: 0,
              totalErrors: 0,
            },
            throughputMetrics: {
              requestsPerMinute: 0,
              requestsPerHour: 0,
              peakRequestsPerMinute: 0,
              averageConcurrentRequests: 0,
            },
          },
        },
        lastActive: new Date(),
        version: "v1.0.0",
        model: "GPT-4",
        tags: ["testing"],
        category: "conversational" as const,
        config: {
          endpoint: "",
          apiKey: "",
          timeout: 30000,
          maxTokens: 4000,
          temperature: 0.7,
          modelVersion: "gpt-4",
          features: [],
        },
        metrics: {
          dailyRequests: 0,
          averageResponseTime: 0,
          errorRate: 0,
          uptime: 0,
          costPerRequest: 0,
          totalCost: 0,
        },
        testResults: {
          totalTests: 0,
          passedTests: 0,
          failedTests: 0,
          lastTestRun: new Date(),
          averageScore: 0,
        },
        testRuns: [],
      });
    }
    setIsLoading(false);
  }, [agentId]);

  // Check if this is the Beauty agent (agentId === "1")
  const isBeautyAgent = agentId === "1";

  const getStatusIcon = (status: string) => {
    switch (status) {
      case "active":
        return <CheckCircle className="h-4 w-4" />;
      case "inactive":
        return <Pause className="h-4 w-4" />;
      case "training":
        return <Loader2 className="h-4 w-4 animate-spin" />;
      case "error":
        return <XCircle className="h-4 w-4" />;
      default:
        return <AlertTriangle className="h-4 w-4" />;
    }
  };

  const tabs = [
    { id: "overview", label: "Overview", path: `/ai-agents/${agentId}` },
    {
      id: "topics",
      label: "Topics",
      path: `/ai-agents/${agentId}/topics`,
    },
    {
      id: "configuration",
      label: "Configuration",
      path: `/ai-agents/${agentId}/configuration`,
    },
  ];

  // Placeholder component for non-Beauty agents
  const PlaceholderContent = () => (
    <div className="flex flex-col items-center justify-center h-full min-h-[400px] text-center">
      <Loader2 className="h-12 w-12 text-pink-400 animate-spin mb-4" />
      <h2 className="text-xl font-semibold text-gray-100 mb-2">
        Tests Being Generated
      </h2>
      <p className="text-gray-400 max-w-md">
        We're currently generating comprehensive tests for this AI agent. This
        process may take a few minutes.
      </p>
      <div className="mt-6 flex items-center space-x-2 text-sm text-gray-500">
        <div className="w-2 h-2 bg-pink-400 rounded-full animate-pulse"></div>
        <span>Preparing test suite...</span>
      </div>
    </div>
  );

  // Show loading state while agent data is being loaded
  if (isLoading || !agent) {
    return (
      <div className="flex h-screen bg-gray-900 items-center justify-center">
        <div className="flex items-center space-x-3">
          <Loader2 className="h-6 w-6 animate-spin text-pink-400" />
          <span className="text-gray-300">Loading agent details...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="flex h-screen bg-gray-900">
      {/* Mobile Sidebar Overlay */}
      {isSidebarOpen && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 z-40 lg:hidden"
          onClick={closeSidebar}
        />
      )}
      {/* Sidebar */}
      <div
        className={cn(
          "fixed lg:relative z-50 h-full transition-transform duration-300 ease-in-out",
          isSidebarOpen
            ? "translate-x-0"
            : "-translate-x-full lg:translate-x-0",
        )}
      >
        <Sidebar />
      </div>
      {/* Main Content */}
      <div className="flex-1 flex flex-col h-full min-h-0 overflow-hidden">
        {/* Top Header */}
        <header className="bg-gray-800 border-b border-gray-700 px-4 lg:px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              {/* Mobile Menu Button */}
              <Button
                variant="ghost"
                size="icon"
                className="lg:hidden text-gray-300 hover:bg-gray-700"
                onClick={toggleSidebar}
              >
                <Menu className="h-5 w-5" />
              </Button>
              <Button
                variant="ghost"
                size="icon"
                onClick={() => {
                  // If on /ai-agents/:agentId (no tab), go to /ai-agents, else go back
                  if (location.pathname.match(/^\/ai-agents\/[^/]+$/)) {
                    navigate({ to: "/ai-agents" });
                  } else {
                    window.history.back();
                  }
                }}
                className="text-gray-400 hover:text-gray-300"
              >
                <ArrowLeft className="h-5 w-5" />
              </Button>
              <div className="flex items-center space-x-3">
                <Avatar className="h-10 w-10">
                  <AvatarFallback className="bg-blue-600 text-blue-100">
                    <Bot className="h-5 w-5" />
                  </AvatarFallback>
                </Avatar>
                <div>
                  <h1 className="text-xl lg:text-2xl font-bold text-gray-100">
                    {agent.name}
                  </h1>
                  <p className="text-sm text-gray-400">{agent.description}</p>
                </div>
              </div>
            </div>
            <div className="flex items-center space-x-4">
              <Button
                variant="outline"
                className="border-gray-600 text-gray-300 hover:bg-gray-700"
              >
                <Settings className="h-4 w-4" />
              </Button>
              <Button
                variant="outline"
                className="border-gray-600 text-gray-300 hover:bg-gray-700"
              >
                <MoreVertical className="h-4 w-4" />
              </Button>
            </div>
          </div>
        </header>
        {/* Navigation Tabs */}
        <div className="bg-gray-800 border-b border-gray-700 px-4 lg:px-6 py-3">
          <div className="flex items-center space-x-2 lg:space-x-6 overflow-x-auto">
            {tabs.map((tab) => (
              <Link
                key={tab.id}
                to={tab.path}
                className={`flex items-center space-x-2 px-3 py-2 rounded-md text-sm font-medium transition-colors whitespace-nowrap ${
                  activeTab === tab.id
                    ? "bg-pink-400 text-white"
                    : "text-gray-400 hover:text-gray-300 hover:bg-gray-700"
                }`}
              >
                <span>{tab.label}</span>
              </Link>
            ))}
          </div>
        </div>
        {/* Main Content Area */}
        <main className="flex-1 h-full flex flex-col min-h-0 bg-gray-900 overflow-auto">
          <div className="p-4 lg:p-6">
            {isBeautyAgent ? children : <PlaceholderContent />}
          </div>
        </main>
      </div>
    </div>
  );
};
